/**
 * LargeFileUploader - Drag & drop upload component for large files (up to 5TB).
 *
 * Uses GCS signed URLs for direct browser-to-cloud upload.
 * Shows per-file and overall progress bars.
 */

import { useState, useCallback, useRef } from 'react';
import {
  uploadLargeFiles,
  type FileUploadProgress,
  type UploadCompleteResponse,
} from '../../services/largeFileUpload';
import { formatFileSize } from '../../utils/dateFormatters';
import styles from './LargeFileUploader.module.css';

interface LargeFileUploaderProps {
  textContent: string | null;
  salespersonId: string | null;
  onUploadComplete: (result: UploadCompleteResponse) => void;
  onError: (message: string) => void;
}

interface QueuedFile {
  file: File;
  id: string;
}

export function LargeFileUploader({
  textContent,
  salespersonId,
  onUploadComplete,
  onError,
}: LargeFileUploaderProps) {
  const [queuedFiles, setQueuedFiles] = useState<QueuedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [fileProgress, setFileProgress] = useState<FileUploadProgress[]>([]);
  const [overallProgress, setOverallProgress] = useState({ loaded: 0, total: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const items = Array.from(newFiles).map((file) => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    }));
    setQueuedFiles((prev) => [...prev, ...items]);
  }, []);

  const removeFile = useCallback((id: string) => {
    setQueuedFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        addFiles(e.target.files);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [addFiles],
  );

  // Drag & drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const handleUpload = useCallback(async () => {
    if (queuedFiles.length === 0) return;

    setIsUploading(true);
    setFileProgress(
      queuedFiles.map((qf) => ({
        filename: qf.file.name,
        loaded: 0,
        total: qf.file.size,
        percent: 0,
        status: 'pending' as const,
      })),
    );
    setOverallProgress({ loaded: 0, total: 0 });

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const result = await uploadLargeFiles({
        files: queuedFiles.map((qf) => qf.file),
        textContent,
        salespersonId,
        onFileProgress: (idx, progress) => {
          setFileProgress((prev) => {
            const next = [...prev];
            next[idx] = progress;
            return next;
          });
        },
        onOverallProgress: (loaded, total) => {
          setOverallProgress({ loaded, total });
        },
        abortSignal: controller.signal,
      });

      onUploadComplete(result);
      setQueuedFiles([]);
      setFileProgress([]);
    } catch (err) {
      if (err instanceof Error && err.message === 'Upload aborted') {
        onError('Upload cancelled.');
      } else {
        onError(
          err instanceof Error ? err.message : 'Upload failed. Please try again.',
        );
      }
    } finally {
      setIsUploading(false);
      abortControllerRef.current = null;
    }
  }, [queuedFiles, textContent, salespersonId, onUploadComplete, onError]);

  const handleCancel = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const totalSize = queuedFiles.reduce((acc, qf) => acc + qf.file.size, 0);
  const overallPercent =
    overallProgress.total > 0
      ? Math.round((overallProgress.loaded / overallProgress.total) * 100)
      : 0;

  return (
    <div className={styles.container}>
      {/* Drop zone / file picker */}
      {!isUploading && (
        <>
          <div
            className={`${styles.dropZone} ${isDragging ? styles.dropZoneActive : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className={styles.hiddenInput}
            />
            <div className={styles.dropZoneIcon}>
              {isDragging ? '\u2B07' : '\u2B06'}
            </div>
            <p className={styles.dropZoneText}>
              {isDragging
                ? 'Drop files here'
                : 'Drag & drop files or click to browse'}
            </p>
            <p className={styles.dropZoneHint}>
              Supports any file type, up to 5 TB per file
            </p>
          </div>

          {/* Queued file list */}
          {queuedFiles.length > 0 && (
            <div className={styles.fileList}>
              <div className={styles.fileListHeader}>
                <span>
                  {queuedFiles.length} file{queuedFiles.length !== 1 ? 's' : ''}{' '}
                  ({formatFileSize(totalSize)})
                </span>
              </div>
              {queuedFiles.map((qf) => (
                <div key={qf.id} className={styles.fileItem}>
                  <span className={styles.fileName}>{qf.file.name}</span>
                  <span className={styles.fileSize}>{formatFileSize(qf.file.size)}</span>
                  <button
                    className={styles.removeBtn}
                    onClick={() => removeFile(qf.id)}
                    aria-label={`Remove ${qf.file.name}`}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Upload progress */}
      {isUploading && (
        <div className={styles.progressSection}>
          {/* Overall progress */}
          <div className={styles.overallProgress}>
            <div className={styles.progressLabel}>
              <span>Uploading... {overallPercent}%</span>
              <span className={styles.progressSize}>
                {formatFileSize(overallProgress.loaded)} / {formatFileSize(overallProgress.total)}
              </span>
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${overallPercent}%` }}
              />
            </div>
          </div>

          {/* Per-file progress */}
          <div className={styles.fileProgressList}>
            {fileProgress.map((fp, i) => (
              <div key={i} className={styles.fileProgressItem}>
                <div className={styles.fileProgressHeader}>
                  <span className={styles.fileProgressName}>{fp.filename}</span>
                  <span className={styles.fileProgressStatus}>
                    {fp.status === 'complete' && '\u2713'}
                    {fp.status === 'uploading' && `${fp.percent}%`}
                    {fp.status === 'pending' && 'Waiting'}
                    {fp.status === 'error' && '\u2717 Error'}
                  </span>
                </div>
                {fp.status === 'uploading' && (
                  <div className={styles.progressBarSmall}>
                    <div
                      className={styles.progressFillSmall}
                      style={{ width: `${fp.percent}%` }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          <button className={styles.cancelBtn} onClick={handleCancel}>
            Cancel Upload
          </button>
        </div>
      )}

      {/* Upload button */}
      {!isUploading && queuedFiles.length > 0 && (
        <button className={styles.uploadBtn} onClick={handleUpload}>
          Upload {queuedFiles.length} file{queuedFiles.length !== 1 ? 's' : ''} (
          {formatFileSize(totalSize)})
        </button>
      )}
    </div>
  );
}
